import { useCallback } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import Spacings from '~/modules/ui-kit/spacings';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';

import messages from './messages';
import styles from './styles.module.css';
import { useAppForm } from '~/components/Form';
import {
  hasConsent,
  hasRespondedToConsent,
  setConsent,
} from '~/utils/analyticsConsent';

type FormValues = {
  deleteNotAssignedApplications?: boolean | null;
  doNotSuggestRecurrentApplications?: boolean | null;
};

type UserProfileSettingsFormProps = {
  onSubmit: (values: FormValues) => void;
  isLoggedInUsersProfile: boolean;
  initialValues: FormValues;
};

const AnalyticsConsentSection = () => {
  const consented = hasConsent('analytics');

  const handleToggle = useCallback(() => {
    setConsent('analytics', !consented);
    // Reload: PostHog has no clean shutdown, and the cookie read isn't reactive
    window.location.reload();
  }, [consented]);

  return (
    <Spacings.Stack scale="xs">
      <FormControlLabel
        control={
          <Checkbox
            checked={consented}
            onChange={handleToggle}
            color="primary"
          />
        }
        label={
          <Typography variant="subtitle1" sx={{ lineHeight: 1.4 }} gutterBottom>
            <FormattedMessage {...messages.analyticsConsentLabel} />
          </Typography>
        }
      />
      <div className={styles.description}>
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage {...messages.analyticsConsentDescription} />
        </Typography>
        {!hasRespondedToConsent() && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            <FormattedMessage {...messages.analyticsConsentNotYetDecided} />
          </Typography>
        )}
      </div>
    </Spacings.Stack>
  );
};

export const UserProfileSettingsForm = (
  props: UserProfileSettingsFormProps
) => {
  const intl = useIntl();

  const form = useAppForm({
    defaultValues: props.initialValues,
    onSubmit: ({ value }) => {
      props.onSubmit(value);
    },
  });

  return (
    <Spacings.Stack scale="xl">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Spacings.Stack scale="m">
          <Typography variant="h6">
            <FormattedMessage {...messages.settings} />
          </Typography>

          <Spacings.Stack scale="xs">
            <form.Field name="deleteNotAssignedApplications">
              {(field) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.state.value === true}
                      onChange={(e) => field.handleChange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={intl.formatMessage(
                    messages.deleteNotAssignedApplicationsLabel
                  )}
                />
              )}
            </form.Field>
            <div className={styles.description}>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ lineHeight: 1.4 }}
                gutterBottom
              >
                <FormattedMessage
                  {...messages.deleteNotAssignedApplicationsDescription}
                />
              </Typography>
            </div>
          </Spacings.Stack>
          <Spacings.Stack scale="xs">
            <form.Field name="doNotSuggestRecurrentApplications">
              {(field) => (
                <FormControlLabel
                  disabled={!props.isLoggedInUsersProfile}
                  control={
                    <Checkbox
                      checked={field.state.value === true}
                      onChange={(e) => field.handleChange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      variant="subtitle1"
                      sx={{ lineHeight: 1.4 }}
                      gutterBottom
                    >
                      <FormattedMessage
                        {...messages.doNotSuggestRecurrentApplications}
                      />
                    </Typography>
                  }
                />
              )}
            </form.Field>
            <div className={styles.description}>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  {...messages.doNotSuggestRecurrentApplicationsDescription}
                />
              </Typography>
            </div>
          </Spacings.Stack>
          <div>
            <Button variant="contained" color="primary" type="submit">
              <FormattedMessage {...messages.saveSettings} />
            </Button>
          </div>
        </Spacings.Stack>
      </form>

      {props.isLoggedInUsersProfile && (
        <>
          <Divider />
          <Spacings.Stack scale="m">
            <Typography variant="h6">
              <FormattedMessage {...messages.analyticsConsentHeading} />
            </Typography>
            <AnalyticsConsentSection />
          </Spacings.Stack>
        </>
      )}
    </Spacings.Stack>
  );
};

export default UserProfileSettingsForm;
